import { Breadcrumb } from '@/components/common/Breadcrumb';
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FileText,
  ArrowRight,
  Calendar,
  User,
  Eye,
  Loader2,
  Search,
  BookOpen,
  TrendingUp,
  ChevronRight,
  Filter,
  X,
  Sparkles,
  Clock,
  Building2,
} from 'lucide-react';
import { articlesAPI, industriesAPI } from '@/services/api';
import { format } from 'date-fns';
import { useDebounce } from '@/hooks/useDebounce';

export default function ArticlesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || '');
  const [featuredArticle, setFeaturedArticle] = useState(null);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    fetchData();
  }, [selectedIndustries, selectedTag, debouncedSearch]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedIndustries.length > 0) params.industry_id = selectedIndustries[0];
      if (selectedTag) params.tag = selectedTag;
      if (debouncedSearch) params.search = debouncedSearch;

      const [articlesRes, industriesRes, tagsRes] = await Promise.all([
        articlesAPI.getAll(params),
        industriesAPI.getAll(),
        articlesAPI.getTags().catch(() => ({ data: [] })),
      ]);

      const allArticles = articlesRes.data?.items || [];
      
      // Get featured article
      const featured = allArticles.find(a => a.is_featured);
      if (featured && !debouncedSearch && selectedIndustries.length === 0 && !selectedTag) {
        setFeaturedArticle(featured);
        setArticles(allArticles.filter(a => a.id !== featured.id));
      } else {
        setFeaturedArticle(null);
        setArticles(allArticles);
      }
      
      setIndustries(industriesRes.data || []);
      setTags(tagsRes.data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleIndustry = (industryId) => {
    setSelectedIndustries(prev => 
      prev.includes(industryId)
        ? prev.filter(id => id !== industryId)
        : [...prev, industryId]
    );
  };

  const clearFilters = () => {
    setSelectedIndustries([]);
    setSelectedTag('');
    setSearch('');
    setSearchParams({});
  };

  const hasActiveFilters = selectedIndustries.length > 0 || selectedTag || search;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return format(new Date(dateStr), 'MMM dd, yyyy');
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="articles-page">
      {/* Hero Section - MailCraft light editorial */}
      <section className="hero-canvas border-b border-zinc-200 pt-28 pb-16 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <Breadcrumb />
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="max-w-3xl">
            <span className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Knowledge Hub
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-[-0.03em] leading-[1.0] mb-4">
              <span className="text-zinc-950">GRC insights </span>
              <span className="text-slate-400">&amp; articles.</span>
            </h1>
            <p className="text-zinc-500 text-lg mb-8">
              Explore expert insights, industry trends, and best practices in Governance, Risk Management, and Compliance.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-xl">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  type="text"
                  placeholder="Search by title, tag, or topic..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-12 pr-4 h-14 bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400 rounded-lg text-lg focus:border-zinc-900"
                  data-testid="article-search"
                />
              </div>
            </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden border border-zinc-200 shadow-[0_24px_64px_-32px_rgba(15,23,42,0.4)]">
                <img
                  src="https://images.unsplash.com/photo-1758876020300-76a782ca51c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="GRC professional reviewing compliance articles and resources"
                  className="w-full h-[380px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article (if exists) */}
      {featuredArticle && (
        <section className="py-8 -mt-8">
          <div className="container-custom">
            <Link to={`/articles/${featuredArticle.slug}`}>
              <Card className="border-0 shadow-2xl overflow-hidden group hover:shadow-3xl transition-all duration-500">
                <div className="grid md:grid-cols-2">
                  <div className="relative h-64 md:h-auto">
                    <img
                      src={featuredArticle.featured_image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80'}
                      alt={featuredArticle.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <Badge className="absolute top-4 left-4 bg-gold-500 text-navy-800">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Featured
                    </Badge>
                  </div>
                  <CardContent className="p-8 md:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(featuredArticle.published_at)}
                      </span>
                      {featuredArticle.author_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {featuredArticle.author_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {featuredArticle.view_count} views
                      </span>
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy-700 mb-4 group-hover:text-gold-600 transition-colors">
                      {featuredArticle.title}
                    </h2>
                    <p className="text-slate-600 mb-6 line-clamp-3">
                      {featuredArticle.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {featuredArticle.industries?.slice(0, 3).map(ind => (
                        <Badge key={ind.id} variant="secondary" className="text-xs">
                          {ind.name}
                        </Badge>
                      ))}
                    </div>
                    <Button className="w-fit bg-navy-600 hover:bg-navy-700 text-white rounded-xl group">
                      Read Article
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </div>
              </Card>
            </Link>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="py-8">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Left Sidebar - Filters */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="sticky top-28 space-y-6">
                
                {/* Industries Filter */}
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold text-navy-700 flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Industries
                      {selectedIndustries.length > 0 && (
                        <Badge className="ml-auto bg-navy-100 text-navy-700">
                          {selectedIndustries.length}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {industries.map((industry) => {
                        const isSelected = selectedIndustries.includes(industry.id);
                        return (
                          <label
                            key={industry.id}
                            className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all ${
                              isSelected 
                                ? 'bg-navy-50 border border-navy-200' 
                                : 'hover:bg-slate-50 border border-transparent'
                            }`}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleIndustry(industry.id)}
                              className="border-slate-300"
                            />
                            <span className={`text-sm ${isSelected ? 'font-medium text-navy-700' : 'text-slate-700'}`}>
                              {industry.name}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Tags Filter */}
                {tags.length > 0 && (
                  <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg font-semibold text-navy-700 flex items-center gap-2">
                        <Filter className="w-5 h-5" />
                        Popular Tags
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {tags.slice(0, 10).map((tag) => (
                          <button
                            key={tag.tag}
                            onClick={() => setSelectedTag(selectedTag === tag.tag ? '' : tag.tag)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              selectedTag === tag.tag
                                ? 'bg-navy-600 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {tag.tag}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="w-full border-slate-300 text-slate-600 hover:text-navy-700"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </aside>

            {/* Main Content - Articles List */}
            <main className="flex-1 min-w-0">
              {/* Active Filters */}
              {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="text-sm text-slate-500">Active filters:</span>
                  {search && (
                    <Badge 
                      className="bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer"
                      onClick={() => setSearch('')}
                    >
                      Search: "{search}" <X className="w-3 h-3 ml-1" />
                    </Badge>
                  )}
                  {selectedIndustries.map(indId => {
                    const ind = industries.find(i => i.id === indId);
                    return ind ? (
                      <Badge 
                        key={indId}
                        className="bg-navy-100 text-navy-700 hover:bg-navy-200 cursor-pointer"
                        onClick={() => toggleIndustry(indId)}
                      >
                        {ind.name} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ) : null;
                  })}
                  {selectedTag && (
                    <Badge 
                      className="bg-purple-100 text-purple-700 hover:bg-purple-200 cursor-pointer"
                      onClick={() => setSelectedTag('')}
                    >
                      Tag: {selectedTag} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  )}
                </div>
              )}

              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-navy-700">
                  {hasActiveFilters ? 'Filtered Results' : 'Latest Articles'}
                </h2>
                <span className="text-slate-500 text-sm">
                  {articles.length} article{articles.length !== 1 ? 's' : ''} found
                </span>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
                </div>
              ) : articles.length > 0 ? (
                <div className="grid gap-6">
                  {articles.map((article) => (
                    <Link key={article.id} to={`/articles/${article.slug}`}>
                      <Card className="group border-slate-200 hover:border-gold-400 hover:shadow-lg transition-all duration-300 overflow-hidden">
                        <div className="flex flex-col sm:flex-row">
                          {/* Thumbnail */}
                          <div className="relative w-full sm:w-56 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
                            <img
                              src={article.thumbnail_url || article.featured_image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80'}
                              alt={article.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            {article.is_featured && (
                              <Badge className="absolute top-3 left-3 bg-gold-500 text-navy-800">
                                <TrendingUp className="w-3 h-3 mr-1" />
                                Featured
                              </Badge>
                            )}
                          </div>
                          
                          {/* Content */}
                          <CardContent className="flex-1 p-5 flex flex-col justify-between">
                            <div>
                              {/* Meta */}
                              <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(article.published_at)}
                                </span>
                                {article.author_name && (
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {article.author_name}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  {article.view_count}
                                </span>
                              </div>
                              
                              {/* Title */}
                              <h3 className="font-heading text-lg font-bold text-navy-700 mb-2 group-hover:text-gold-600 transition-colors line-clamp-2">
                                {article.title}
                              </h3>
                              
                              {/* Excerpt */}
                              <p className="text-slate-600 text-sm line-clamp-2 mb-3">
                                {article.excerpt}
                              </p>
                              
                              {/* Tags/Industries */}
                              <div className="flex flex-wrap gap-2">
                                {article.industries?.slice(0, 2).map(ind => (
                                  <Badge key={ind.id} variant="secondary" className="text-xs">
                                    {ind.name}
                                  </Badge>
                                ))}
                                {(article.tags || []).slice(0, 2).map(tag => (
                                  <Badge key={tag} className="text-xs bg-slate-100 text-slate-600">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            
                            {/* Read more */}
                            <div className="mt-4 pt-4 border-t border-slate-100">
                              <span className="text-navy-600 text-sm font-medium flex items-center gap-1 group-hover:text-gold-600 transition-colors">
                                Read Article
                                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                              </span>
                            </div>
                          </CardContent>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              ) : (
                <Card className="border-slate-200">
                  <CardContent className="p-12 text-center">
                    <BookOpen className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <h3 className="font-heading text-xl font-semibold text-navy-700 mb-2">
                      No Articles Found
                    </h3>
                    <p className="text-slate-600 mb-6">
                      {hasActiveFilters 
                        ? 'Try adjusting your filters to find more articles.'
                        : 'New articles are being prepared. Check back soon!'}
                    </p>
                    {hasActiveFilters && (
                      <Button onClick={clearFilters} className="bg-navy-600 hover:bg-navy-700 text-white rounded-xl">
                        Clear Filters
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </main>
          </div>
        </div>
      </section>
    </div>
  );
}
